import {
  Box,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { IGetUsers, ITemporaryPassword } from "../../../models/users";
import { useTranslation } from "react-i18next";
import { useFetch, useNotificationContext } from "@4uhub/lib4uhub";
import MainApiService from "../../../services/mainApi.service";
import { memo, useCallback, useEffect, useState } from "react";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";

interface IUserTemporaryPasswordModal {
  user: IGetUsers;
}

const translationPath = "page.register.users.temporary_password.";

const TEMPORARY_PASSWORD =
  process.env.REACT_APP_IDENTIY + "/api/v1/Users/CreateUserTemporaryPassword";
const temporaryPasswordService = new MainApiService<ITemporaryPassword>(
  TEMPORARY_PASSWORD
);

export const UserTemporaryPasswordModal = memo(
  ({ user }: IUserTemporaryPasswordModal) => {
    const { t } = useTranslation();
    const [temporaryPassword, setTemporaryPassword] = useState<string | null>(
      null
    );
    const { setMessage } = useNotificationContext();
    const { sendRequest, loading } = useFetch(temporaryPasswordService.create);

    const fetchTemporaryPassword = useCallback(async () => {
      if (user.id) {
        const { data, success } = await sendRequest({
          userId: user.id,
        });
        if (data && success) {
          setTemporaryPassword(data.password);
        }
      }
    }, [sendRequest, user]);

    useEffect(() => {
      fetchTemporaryPassword();
    }, [fetchTemporaryPassword]);

    const copyPasswordHandler = useCallback(() => {
      if (temporaryPassword) {
        navigator.clipboard.writeText(temporaryPassword);
        setMessage({
          message: t(translationPath + "password_copied"),
          type: "success",
        });
      }
    }, [temporaryPassword, setMessage, t]);

    if (loading) {
      return (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 2,
          }}
        >
          <CircularProgress />
        </Box>
      );
    }

    return (
      <Box
        sx={{ display: "flex", flexDirection: "column", alignItems: "center" }}
      >
        <Typography variant="h6">{t(translationPath + "title")}:</Typography>
        {temporaryPassword && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: "bold" }}>
              {temporaryPassword}
            </Typography>
            <Tooltip title={t(translationPath + "copy")}>
              <IconButton onClick={copyPasswordHandler}>
                <ContentCopyRoundedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>
    );
  }
);
